/**
 * MobileDrawer.tsx
 * Phase 1p Mobile Nav + A11y SSOT — Wave 3 Stage 2
 * Issue #3028
 *
 * Phase 1n Drawer wrapper for full menu navigation on mobile.
 * 全メニュー (sidebar と同等) + Setup / ヘルプ
 *
 * Foundation:
 * - Phase 1n Drawer (@/components/feedback/Drawer) を活用
 * - オーバーレイ時の背景スクロール禁止は Phase 1n Sheet (SheetContent) が担保済み
 *
 * A11y:
 * - aria-label="メインメニュー"
 * - .ripia-focus-ring utility 適用 (Phase 1p SSOT)
 *
 * Scope OUT: gesture / PWA / オフライン (Tier 2)
 */

import * as React from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  Users,
  UserCog,
  TrendingUp,
  Sparkles,
  ClipboardList,
  Settings,
  HelpCircle,
  HeartHandshake,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Drawer } from '@/components/feedback/Drawer';
import { useFeatureAccess } from '@/components/plan/FeatureGate';
import { isRepeatEnabled } from '@/lib/repeatGate';
import { useIsRepeatFirstOwner } from '@/hooks/repeat/useIsRepeatFirstOwner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MobileDrawerProps {
  /** 表示状態 */
  open: boolean;
  /** 閉じるコールバック */
  onOpenChange: (open: boolean) => void;
}

interface DrawerNavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  external?: boolean;
}

interface DrawerNavGroup {
  groupLabel: string;
  items: DrawerNavItem[];
}

// ---------------------------------------------------------------------------
// Navigation structure (sidebar 相当の全メニュー + Setup / ヘルプ)
// ---------------------------------------------------------------------------

const MAIN_NAV_GROUPS: DrawerNavGroup[] = [
  {
    groupLabel: 'メインメニュー',
    items: [
      { label: 'ダッシュボード', icon: LayoutDashboard, to: '/dashboard' },
      { label: '予約管理', icon: Calendar, to: '/reservations' },
      { label: '顧客管理', icon: Users, to: '/customers' },
      { label: 'カルテ管理', icon: ClipboardList, to: '/treatments' },
      { label: 'スタッフ', icon: UserCog, to: '/staff' },
      { label: '売上分析', icon: TrendingUp, to: '/sales' },
      { label: 'AI分析', icon: Sparkles, to: '/ai-analysis' },
    ],
  },
  {
    groupLabel: 'サポート',
    items: [
      { label: '設定', icon: Settings, to: '/settings' },
      { label: 'ヘルプ', icon: HelpCircle, to: '/help' },
    ],
  },
];

// ---------------------------------------------------------------------------
// DrawerNavLink component
// ---------------------------------------------------------------------------

function DrawerNavLink({
  item,
  isActive,
  onClose,
}: {
  item: DrawerNavItem;
  isActive: boolean;
  onClose: () => void;
}): React.ReactElement {
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      onClick={onClose}
      className={cn(
        'ripia-focus-ring',
        'flex items-center gap-3 rounded-md px-3 py-2.5',
        'text-sm font-medium',
        'transition-colors duration-[var(--dur-fast)]',
        isActive
          ? 'bg-[var(--brand-soft)] text-[var(--brand-ink)]'
          : 'text-[var(--ink-2)] hover:bg-[var(--brand-hover)] hover:text-[var(--ink)]'
      )}
      aria-current={isActive ? 'page' : undefined}
    >
      <Icon
        className={cn(
          'h-5 w-5 shrink-0',
          isActive ? 'text-[var(--brand)]' : 'text-[var(--ink-3)]'
        )}
      />
      <span>{item.label}</span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// MobileDrawer component
// ---------------------------------------------------------------------------

/**
 * MobileDrawer
 *
 * mobile viewport (md 未満) のフルメニュー Drawer。
 * Phase 1n Drawer wrapper。sidebar 相当の全メニュー + Setup / ヘルプ。
 * オーバーレイ時の背景スクロール禁止は Phase 1n Sheet (SheetContent) が担保。
 *
 * @example
 * ```tsx
 * <MobileDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
 * ```
 */
export function MobileDrawer({
  open,
  onOpenChange,
}: MobileDrawerProps): React.ReactElement {
  const location = useLocation();
  const repeatFeatureAccess = useFeatureAccess('repeat');
  const canShowRepeat = isRepeatEnabled() && repeatFeatureAccess;
  // #4038 / HAM-3471 案B: repeat-first owner には /dashboard 導線を出さない（staff は維持）
  const isRepeatFirstOwner = useIsRepeatFirstOwner();
  const navGroups = React.useMemo<DrawerNavGroup[]>(() => {
    return MAIN_NAV_GROUPS.map((group) => {
      if (group.groupLabel !== 'メインメニュー') {
        return group;
      }

      let items = [...group.items];

      // 案B: repeat-first owner は /dashboard を隠す（gate で bounce されるだけの導線）
      if (isRepeatFirstOwner) {
        items = items.filter((item) => item.to !== '/dashboard');
      }

      // 「リピート作成」を「スタッフ」の直前に挿入（plan+flag gate 通過時のみ）。
      // dashboard 除去で index がずれても追従するよう findIndex で位置決め。
      if (canShowRepeat) {
        const staffIndex = items.findIndex((item) => item.to === '/staff');
        const insertAt = staffIndex === -1 ? items.length : staffIndex;
        items.splice(insertAt, 0, {
          label: 'リピート作成',
          icon: HeartHandshake,
          to: '/repeat',
        });
      }

      return { ...group, items };
    });
  }, [canShowRepeat, isRepeatFirstOwner]);

  const handleClose = React.useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const isActive = React.useCallback(
    (to: string) => {
      if (to === '/dashboard') {
        return location.pathname === '/dashboard' || location.pathname === '/';
      }
      return location.pathname.startsWith(to);
    },
    [location.pathname]
  );

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold text-[var(--ink)]">
            メニュー
          </span>
          <button
            type="button"
            onClick={handleClose}
            className={cn(
              'ripia-focus-ring',
              'flex h-8 w-8 items-center justify-center rounded-md',
              'text-[var(--ink-3)] hover:text-[var(--ink)]',
              'hover:bg-[var(--brand-hover)]',
              'transition-colors duration-[var(--dur-fast)]'
            )}
            aria-label="メニューを閉じる"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      }
      widthClassName="w-4/5 sm:max-w-xs"
      className="flex flex-col"
    >
      <nav aria-label="メインメニュー" data-testid="mobile-drawer">
        {navGroups.map((group) => (
          <div key={group.groupLabel} className="py-2">
            <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-[var(--ink-3)]">
              {group.groupLabel}
            </p>
            <ul className="space-y-0.5" role="list">
              {group.items.map((item) => (
                <li key={item.to}>
                  <DrawerNavLink
                    item={item}
                    isActive={isActive(item.to)}
                    onClose={handleClose}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </Drawer>
  );
}
